import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { pushRecentlyViewed, incrementViewCount } from "../../utils/cookies";
import { trackView, trackDwellTime, trackScrollDepth } from "../../utils/analytics";
import { API_BASE } from "../../api/api";
import { productIdFromSlug, productQueryOptions } from "../../api/products";
import { useProductViewer } from "../../hooks/useProductViewer";
import { catalogueProductSchema, publicPath, publicPrice } from "../../utils/catalogueSeo";
import { useSEO, BASE_URL } from "../../utils/seo";
import ProductGallery from "./ProductGallery";
import ProductInfo from "./ProductInfo";
import SpecTable from "./SpecTable";
import Reviews from "./Reviews";
import ProductRail from "./ProductRail";
import HowToUseSteps from "./HowToUseSteps";
import "./ProductDetailPage.css";

// ~155 characters is what Google shows before truncating; cutting on a word
// boundary keeps the last word whole.
const META_DESC_MAX = 155;
export const truncateForMeta = (text) => {
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= META_DESC_MAX) return clean;
  const cut = clean.slice(0, META_DESC_MAX);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 80 ? cut.slice(0, lastSpace) : cut).trim()}…`;
};

const ProductDetailPage = ({ onAddToCart }) => {
  const { slug } = useParams();

  const [selection, setSelection] = useState(null);
  const [reviews, setReviews] = useState([]);

  // Fetched as the signed-in shopper (see useProductViewer): an age-verified
  // adult can open an age-restricted product; everyone else still gets the
  // server's 403 → "Not Available" below. Held until auth resolves so a
  // verified adult's first load never goes out anonymously — isPending (not
  // isLoading) keeps the loading state up during that wait instead of
  // briefly rendering "not found".
  const viewer = useProductViewer();
  const {
    data: product,
    isPending: loading,
    isError,
    error,
    refetch,
  } = useQuery({
    ...productQueryOptions(slug, viewer),
    enabled: viewer.ready,
    retry: false, // a 404 isn't worth retrying
  });
  const ageRestricted = isError && error?.ageRestricted;
  const notFound = isError && error?.notFound;
  const unavailable = isError && !notFound && !ageRestricted;
  const selected = useMemo(() => product && selection && (product.variants || []).some((v) => v.id === selection.id)
    ? { ...product, ...selection } : product, [product, selection]);

  // Which product's reviews were last asked for. A reply for any other
  // product (the shopper moved on) is dropped.
  const reviewsForRef = useRef(null);
  const fetchReviews = useCallback(async (productId) => {
    reviewsForRef.current = productId;
    try {
      const res = await fetch(`${API_BASE}/products/${productId}/reviews?limit=50`);
      if (res.ok) {
        const data = await res.json();
        if (reviewsForRef.current === productId) setReviews(data);
      }
    } catch { /* non-fatal */ }
  }, []);

  // Reviews start with the product, not after it: the id is in the slug
  // (speed brief item 22). If the product turns out to have another id, the
  // effect below asks again for the right one.
  const slugProductId = productIdFromSlug(slug);
  useEffect(() => {
    if (slugProductId && reviewsForRef.current !== slugProductId) fetchReviews(slugProductId);
  }, [slugProductId, fetchReviews]);

  // Side effects that used to live in the fetch's .then() — now driven off
  // the query result. Guarded so they fire once per product, not on every
  // re-render (e.g. a background refetch landing the same data).
  const trackedIdRef = useRef(null);
  useEffect(() => {
    if (!product || trackedIdRef.current === product.id) return;
    trackedIdRef.current = product.id;
    pushRecentlyViewed(product.id);
    incrementViewCount(product.id);
    trackView(product.id, product.category, null, product.price);
    if (reviewsForRef.current !== product.id) fetchReviews(product.id);
  }, [product, fetchReviews]);

  // Dwell time + scroll depth, fired on unmount/navigation away. Dwell only
  // counts foreground time (Page Visibility API) — a backgrounded tab left
  // open for minutes shouldn't record minutes of "attention".
  const productId = product?.id;
  useEffect(() => {
    if (!productId) return;
    const path = `/product/${slug}`;
    let maxScroll = 0;
    let accumulatedMs = 0;
    let visibleSince = document.hidden ? null : Date.now();

    const handleScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - doc.clientHeight;
      const pct = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
      if (pct > maxScroll) maxScroll = pct;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (visibleSince !== null) {
          accumulatedMs += Date.now() - visibleSince;
          visibleSince = null;
        }
      } else {
        visibleSince = Date.now();
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (visibleSince !== null) accumulatedMs += Date.now() - visibleSince;
      trackDwellTime(path, accumulatedMs / 1000);
      trackScrollDepth(path, maxScroll);
    };
  }, [productId, slug]);

  const schema = useMemo(() => {
    if (!selected) return null;
    const canonical = `${BASE_URL}${publicPath(product)}`;
    return { "@context": "https://schema.org", "@graph": [
      catalogueProductSchema(selected, `${BASE_URL}${publicPath(selected)}`),
      { "@type": "BreadcrumbList", itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
        { "@type": "ListItem", position: 2, name: product.name, item: canonical },
      ] },
    ] };
  }, [product, selected]);

  // "{Product name} – KES {price}", with useSEO appending "| Emporia".
  // KES (the ISO code) rather than "Ksh" because this string is read by
  // search engines as much as by people; the on-page price display is
  // unchanged and still uses PriceDisplay's Ksh formatting.
  const titleForMeta = product
    ? `${product.name} – KES ${publicPrice(selected.price).toLocaleString("en-KE")}`
    : notFound ? "Product not found" : unavailable ? "Service temporarily unavailable" : null;
  // The product's OWN words, trimmed to ~155 characters on a word boundary
  // so the snippet is not cut mid-word. Falls back to a factual sentence
  // rather than a marketing claim when a product has no description.
  const descForMeta = product
    ? truncateForMeta(product.description || `${product.name}. Pay securely with M-Pesa and get delivery across Kenya.`)
    : null;

  useSEO({
    title: titleForMeta,
    description: descForMeta,
    path: product ? publicPath(product) : `/product/${slug}`,
    noindex: notFound || ageRestricted,
    image: product?.image_url && !product.image_url.startsWith("data:")
      ? product.image_url
      : undefined,
    schema,
  });

  const handleAddToCart = (prod, qty) => {
    onAddToCart(prod, qty);
  };

  if (loading) {
    return (
      <main className="pdp-shell">
        <div className="pdp-skeleton" aria-busy="true" aria-label="Loading product…">
          <div className="pdp-skel-gallery" />
          <div className="pdp-skel-info">
            <div className="pdp-skel-line w60" />
            <div className="pdp-skel-line w40" />
            <div className="pdp-skel-line w80" />
            <div className="pdp-skel-line w50" />
          </div>
        </div>
      </main>
    );
  }

  if (ageRestricted) {
    return (
      <main className="pdp-shell pdp-not-found">
        <h1>Not Available</h1>
        <p>This product requires a signed-in account with a verified adult age.</p>
        <Link to="/" className="pdp-back-link">← Back to Home</Link>
      </main>
    );
  }

  if (unavailable) {
    return <main className="pdp-shell pdp-not-found"><h1>Temporarily unavailable</h1><p>We could not load this product. Please try again.</p><button type="button" onClick={() => refetch()}>Retry</button></main>;
  }

  if (notFound || !product) {
    return (
      <main className="pdp-shell pdp-not-found">
        <h1>Product not found</h1>
        <p>This product may have been removed or the link is incorrect.</p>
        <Link to="/" className="pdp-back-link">← Back to Home</Link>
      </main>
    );
  }

  // Build ordered images array from all 5 image fields.
  // Keep data: URIs — the gallery renders them fine. Only collapse empty/null slots.
  const galleryImages = [
    product.image_url,
    product.image_url_2,
    product.image_url_3,
    product.image_url_4,
    product.image_url_5,
  ].map((u) => u || null);

  return (
    <main className="pdp-shell">
      {/* Breadcrumb */}
      <nav className="pdp-breadcrumb" aria-label="Breadcrumb">
        <Link to="/">Home</Link>
        <span aria-hidden="true"> / </span>
        {product.category && (
          <>
            <Link to={`/category/${product.category}`}>{product.category}</Link>
            <span aria-hidden="true"> / </span>
          </>
        )}
        <span aria-current="page">{product.name}</span>
      </nav>

      {/* Two-column: gallery + info */}
      <div className="pdp-top">
        <div className="pdp-gallery-col">
          <ProductGallery images={galleryImages} videoUrl={product.video_url} productName={product.name} productId={product.id} />
        </div>
        <div className="pdp-info-col">
          <ProductInfo
            product={product}
            onSelectionChange={setSelection}
            onAddToCart={handleAddToCart}
          />
        </div>
      </div>

      {/* Below sections */}
      <div className="pdp-below">
        {product.description && (
          <section className="pdp-description" aria-labelledby="pdp-desc-heading">
            <h2 className="pdp-desc-heading" id="pdp-desc-heading">Description</h2>
            <p className="pdp-desc-text">{product.description}</p>
          </section>
        )}
        <HowToUseSteps />
        <SpecTable product={product} />
        <Reviews
          productId={product.id}
          reviews={reviews}
          averageRating={product.average_rating}
          reviewCount={product.review_count}
          onReviewSubmitted={() => fetchReviews(product.id)}
        />
      </div>

      {/* You Might Also Like rail */}
      <div className="pdp-popular">
        <ProductRail
          eyebrow="Just For You"
          title="You Might Also Like"
          endpoint={`/products/${product.id}/related?limit=4`}
          cols={4}
          onAddToCart={onAddToCart}
          hideIfEmpty
        />
      </div>
    </main>
  );
};

export default ProductDetailPage;
